import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/FirebaseAuth.js";
import { useNavigate, useParams } from "react-router-dom";
import { getUserById } from "../models/UserFunctions.js";
import { getOrgById, getOrgByRef } from "../models/OrgFunctions.js";
import Column from "../layout/Column.js";
import Row from "../layout/Row.js";
import Divider from "../layout/Divider.js";
import Icon1 from "../icons/Icon.js";
import Space from "../layout/Space.js";
import InfoCell from "../listviews/InfoCell.js";
import {
  getDocDataById,
  getDocDataByRef,
  getFlutterTime,
  getLocalStringFromFlutter,
  queryCollection,
  updateDocDataField,
} from "../models/DataFunctions.js";

import "../animations/animations.css";
import { useTheme } from "@mui/material";
import { getOrRefreshFirebaseToken } from "../config/getFirebaseToken.js";

import InvoicesAM from "../actionmenus/InvoiceAM.js";
import getTemporaryUrl from "../api/getTemporaryUrl.js";
import { useAppContext } from "../config/AppContext.js";
import PDF from "../pdf/PDF.js";
import Modal1 from "../modals/Modal1.js";
import { useSnackbar } from "notistack";

import updateDocDataFieldApi from "../api/database/updateDocDataFieldApi.js";
import { EditableInfoCell } from "./folder_pages/folder_sidebar/FolderInformationSection.js";
import sendDVemail from "../api/sign/sendDVemail.js";
import PrimaryButton from "../buttons/PrimaryButton.js";
import SecondaryButton from "../buttons/SecondaryButton.js";
import { formatToEuro } from "./NewInvoiceDraft.js";
import EmailModal from "../modals/EmailModal.js";
import { format } from "date-fns";
import SmallSpinner from "../loading/SmallSpinner.js";
import NotesDevis from "../notes/NotesDevis.js";
import DevisPennylane from "../folder_modals/DevisPennylane.js";
import PennylaneDevis from "../folder_modals/PennylaneDevis.js";

const Invoice = ({ userData, orgData, navigateWorkspace, workspaceData }) => {
  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const { id } = useParams(); // Access the `id` parameter from the URL
  const { currentUser, loading } = useAuth();
  const [createFolderOpen, setCreateFolderOpen] = React.useState(false);

  const [masterData, setMasterData] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [emails, setEmails] = useState([]);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [openedEmails, setOpenedEmails] = useState({});
  // Initialize access states (all false by default) in the parent

  // Fetch user, org, clients, and article data
  const handleFetchData = async () => {
    if (userData) {
      try {
        if (!workspaceData) {
          const master = orgData;
          setMasterData(master);
        } else if (workspaceData) {
          const master = await getOrgById(workspaceData.master_id);
          setMasterData(master);
        }
        const token = await getOrRefreshFirebaseToken();
        await getTemporaryUrl(token, "devis", "", id, prefix);
        const invoice = await getDocDataById("devis", id);
        if (invoice.history_events === undefined) {
          invoice.history_events = [
            {
              name: "Création",
              date: getFlutterTime(),
            },

            {
              name: "Envoi",
              date: getFlutterTime(),
            },

            {
              name: "Signature",
              date: getFlutterTime(),
            },
          ];
        }
        let filters = [
          {
            field: "invoice_ref",
            operator: "==",
            value: invoice.ref,
          },
        ];
        const emails = await queryCollection("email_sent", filters);
        setEmails(emails);
        setInvoiceData(invoice);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const handleViewFile = async (file) => {
    const token = await getOrRefreshFirebaseToken();
    await getTemporaryUrl(token, "devis", "", invoiceData.id, prefix);
    const new_invoice = await getDocDataById("devis", id);
    setInvoiceData(new_invoice);
    let plans_files = new_invoice.plans_files;
    let matching_file = plans_files.find((f) => f.name === file.name);
    matching_file.url
      ? window.open(matching_file.url, "_blank")
      : handleClickVariant("Le fichier n'est pas disponible", "error");
  };

  const handleGenerateDevis = async () => {
    navigateWorkspace("/newinvoicefromdraft/" + id);
  };

  const handleOpenPdf = async () => {
    const token = await getOrRefreshFirebaseToken();
    await getTemporaryUrl(token, "devis", "", id, prefix);
    const new_invoice = await getDocDataById("devis", id);
    setInvoiceData(new_invoice);
    if (!new_invoice.file?.url) {
      handleClickVariant("Le fichier n'est pas disponible", "error");
      return;
    }
    new_invoice.file.url
      ? setPDFOpen([new_invoice.file])
      : handleClickVariant("Le fichier n'est pas disponible", "error");
  };
  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const handleChangeNumDevis = (e) => {
    setInvoiceData({ ...invoiceData, num_devis: e.target.value });
  };

  const handleSyncNumDevis = async () => {
    try {
      await updateDocDataFieldApi(
        invoiceData.ref,
        "num_devis",
        invoiceData.num_devis,
        prefix
      );
      handleClickVariant("Numéro de devis mis à jour", "success");
    } catch (error) {
      console.error("Error updating num_devis:", error);
      handleClickVariant("Erreur lors de la mise à jour", "error");
    }
  };

  const handleChangeNotes = (e) => {
    setInvoiceData({ ...invoiceData, notes: e.target.value });
  };

  const handleSyncNotes = async () => {
    try {
      await updateDocDataFieldApi(
        invoiceData.ref,
        "notes",
        invoiceData.notes || "",
        prefix
      );
      handleClickVariant("Notes mises à jour", "success");
      setIsEditingNotes(false);
    } catch (error) {
      console.error("Error updating notes:", error);
      handleClickVariant("Erreur lors de la mise à jour", "error");
    }
  };

  const theme = useTheme();

  const [pdf_open, setPDFOpen] = useState(null);

  useEffect(() => {
    if (currentUser) {
      handleFetchData(); // Fetch user data when currentUser is available
    }
  }, [currentUser]); // Dependency array ensures this runs when currentUser changes

  // Handle navigation to clients page
  const gotoInvoices = () => {
    navigateWorkspace("/invoices");
  };

  if (loading || !currentUser || !orgData || !invoiceData) {
    return (
      <div className="w-full h-full flex flex-col justify-center items-center">
        <SmallSpinner></SmallSpinner>
      </div>
    ); // Show loading state while checking auth or if currentUser is null
  }

  return (
    <div className="w-full h-full overflow-auto flex flex-col gap-2">
      <div className="flex flex-row gap-4 w-full h-fit">
        <div className="flex flex-col max-w-c1k gap-1 w-full h-fit pb-40">
          <Modal1 isOpen={pdf_open} onClose={() => setPDFOpen(null)}>
            <div className="flex flex-col w-c900 h-fit bg-neutral-100 rounded-md">
              {" "}
              <PDF files={pdf_open} onClose={() => setPDFOpen(null)}></PDF>
            </div>
          </Modal1>
          <Modal1 isOpen={emailOpen} onClose={() => setEmailOpen(false)}>
            <EmailModal
              invoiceData={invoiceData}
              isOpen={emailOpen}
              onClose={(wanted = false) => {
                if (wanted) {
                  setEmailOpen(false);
                  handleFetchData();
                }
              }}
              orgData={orgData}
              masterData={masterData}
              navigateWorkspace={navigateWorkspace}
              workspaceData={workspaceData}
            ></EmailModal>
          </Modal1>

          {/* Left Column: Takes all available space */}
          <div className="flex flex-col gap-1 h-full flex-grow pl-4 text-h3">
            <Space y={20} />
            <div className="flex flex-row gap-1 w-full  ">
              <div
                className="flex flex-row gap-1  w-full cursor-pointer"
                onClick={gotoInvoices}
              >
                <div className="text-h2 font-semibold text-primary_l bg-transparent">
                  Devis
                </div>
                <Icon1 icon_name="right" height="15px"></Icon1>
              </div>

              <div className="flex w-fit min-w-fit">
                {invoiceData?.state !== "draft" && (
                  <SecondaryButton className="w-fit" onClick={handleOpenPdf}>
                    Voir le devis
                  </SecondaryButton>
                )}

                <Space x={10} />
                {(invoiceData?.state !== "draft" || !workspaceData) && (
                  <InvoicesAM
                    orgData={orgData}
                    userData={userData}
                    invoiceData={invoiceData}
                    handleRefresh={handleFetchData}
                    navigateWorkspace={navigateWorkspace}
                    workspaceData={workspaceData}
                    masterData={masterData}
                    createFolderOpen={createFolderOpen}
                    setCreateFolderOpen={setCreateFolderOpen}
                    setEmailOpen={setEmailOpen}
                  />
                )}
              </div>
            </div>
            <Space y={5} />
            <div className="text-hx font-semibold">{invoiceData?.nom}</div>{" "}
            <Space y={5} />
            {invoiceData.state === "draft" && (
              <div className="flex flex-col mb-4 gap-2">
                <div className="flex flex-row px-2 py-0.5 items-center rounded-xl bg-neutral-500 text-white w-fit">
                  Brouillon
                </div>

                <div className="flex flex-row px-2 py-1 border border-orange-200 items-center rounded-md bg-orange-50 text-orange-500  w-fit">
                  En attente de finalisation du brouillon par {masterData?.name}
                </div>

                {!workspaceData && (
                  <PrimaryButton
                    onClick={handleGenerateDevis}
                    className={"w-fit"}
                  >
                    Générer le devis à partir du brouillon
                  </PrimaryButton>
                )}
              </div>
            )}
            {invoiceData.state === "locked" && (
              <div className="flex flex-row gap-2">
                <div className="flex flex-row px-2 py-0.5 items-center rounded-xl bg-neutral-400 text-white w-fit">
                  Pas encore envoyé
                </div>
                <PrimaryButton
                  onClick={() => setEmailOpen(true)}
                  className={"w-fit"}
                >
                  Envoyer le devis à signer
                </PrimaryButton>
              </div>
            )}
            {invoiceData.state === "done" && (
              <div className="flex flex-row gap-2">
                <div className="flex flex-row px-2 py-0.5 items-center rounded-xl bg-green-100 text-green-600 w-fit">
                  Devis signé
                </div>
                {!invoiceData?.folder_ref ? (
                  <></>
                ) : (
                  <PrimaryButton
                    onClick={() =>
                      navigateWorkspace("/folder/" + invoiceData.folder_ref.id)
                    }
                    className={"w-fit"}
                  >
                    Aller au dossier
                  </PrimaryButton>
                )}
              </div>
            )}
            {invoiceData.state === "wait" && (
              <div className="flex flex-row px-2 py-0.5 items-center rounded-xl bg-orange-300 text-white w-fit">
                En attente
              </div>
            )}
            {invoiceData.state === "error" && (
              <div className="flex flex-row px-2 py-0.5 items-center rounded-xl bg-red-300 text-white w-fit">
                Erreur
              </div>
            )}
            {invoiceData.state === "cancelled" && (
              <div className="flex flex-row px-2 py-0.5 items-center rounded-xl bg-red-300 text-white w-fit">
                Annulé
              </div>
            )}
            <Space y={5} />
            <div className="w-full flex flex-row gap-2">
              <div className=" font-semibold text-pr bg-transparent">
                Auteur
              </div>
              <div className="font-light text-pr bg-transparent">
                {invoiceData?.author === "website"
                  ? "Particulier depuis le site web"
                  : invoiceData?.author === "master"
                    ? "Infiltromètre depuis hexagone"
                    : invoiceData?.author === "website_oec_form"
                      ? "Client depuis Formulaire OEC"
                      : "Client depuis hexagone"}
              </div>
            </div>{" "}
            <div className="flex flex-row gap-2 w-full">
              <div className=" font-semibold text-pr bg-transparent">
                Client
              </div>

              <div
                className={`inline-flex h-5  items-center rounded-full px-2 py-1 flex-row text-blue-400  bg-blue-50   font-light `}
              >
                {invoiceData?.marque}
              </div>
            </div>
            <div className="w-full flex flex-row gap-2 text-h3">
              <div className="font-semibold text-pr bg-transparent">
                Date de création
              </div>
              <div className=" font-light text-pr bg-transparent">
                {invoiceData?.date}
              </div>
            </div>
            <div className="w-full flex flex-row gap-2">
              <div className="font-semibold text-pr bg-transparent">
                Date de signature
              </div>
              <div className=" font-light text-pr bg-transparent">
                {getLocalStringFromFlutter(invoiceData?.sign_date)}
              </div>
            </div>{" "}
            <div className="w-full flex flex-col gap-2">
              <div className="font-semibold text-pr bg-transparent">
                Commentaires
              </div>
              <div className=" font-light text-pr bg-transparent text-shadow_50 whitespace-pre-wrap">
                {invoiceData?.comments}
              </div>
            </div>
            <Space y={20} />
            <div className="text-h1 font-semibold">Historique</div>
            <Space y={10} />
            {invoiceData?.history_events?.map((event, index) => (
              <div key={index} className="flex flex-col gap-0">
                {index !== 0 && (
                  <div
                    className="h-6 border-l-1 border-shadow_25 w-0"
                    style={{
                      marginLeft: "5px",
                    }}
                  ></div>
                )}
                <div className="flex flex-row h-1 mt-1 gap-2 items-center ">
                  <div className="rounded-full border-1 border-shadow_50 h-3 w-3 "></div>{" "}
                  <div className="flex text-h3 text-shadow_50 w-c150">
                    {new Date(event.date.seconds * 1000).toLocaleString()}
                  </div>
                  <div className="flex text-h3 text-shadow_50">
                    {event.name}
                  </div>
                </div>
              </div>
            ))}
            <Space y={20} />
            {invoiceData?.state !== "draft" && (
              <>
                {" "}
                <div className="text-h1 font-semibold">
                  Articles sélectionnés
                </div>
                <Space y={5} />
                <Space y={10} />
                <div className="flex flex-col gap-2 w-full">
                  {invoiceData.articles?.map((article, index) => (
                    <div
                      className="flex flex-row justify-between w-full gap-2"
                      key={index}
                    >
                      <div className="text-h3 text-shadow_50 text-left">
                        {article.name}
                      </div>
                      <div className="flex flex-row gap-2">
                        <div className="text-h3 text-shadow_50 text-left">
                          {"("}
                          {invoiceData.nombre_maisons}
                          {") "}
                          {article.saved_remise &&
                            article.saved_remise !== 0 && (
                              <>
                                {" "}
                                {"(-"}
                                {article.saved_remise || 0}
                                {"%)"}
                              </>
                            )}{" "}
                          {formatToEuro(article.saved_np || 0)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>{" "}
                <Space y={20} />
              </>
            )}
            <div className="text-h1 font-semibold"> Fichiers déposés</div>
            <Space y={5} />
            {invoiceData?.plans_files &&
              invoiceData?.plans_files?.length > 0 && (
                <div className="flex flex-col gap-2 w-full mb-4">
                  {invoiceData?.plans_files?.map((file, index) => (
                    <div key={index} className="flex w-full">
                      <div className="flex flex-row items-center gap-2">
                        <div className="text-h3 font-medium w-c100">
                          {"Fichier " + (index + 1)}
                        </div>
                        <SecondaryButton
                          className={"w-fit"}
                          onClick={() => handleViewFile(file)}
                        >
                          Ouvrir
                        </SecondaryButton>

                        <div className="text-h3 rounded-md py-1 h-fit min-h-7 px-2 mr-2  flex-grow   font-light text-pr bg-shadow_4">
                          {limitStringSize(file.name)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            <div className="text-h1 font-semibold"> Devis Pennylane liés</div>
            <PennylaneDevis
              devisData={invoiceData}
              orgData={orgData}
              userData={userData}
              handleRefresh={handleFetchData}
            ></PennylaneDevis>{" "}
            <Space y={15} />
            <div className="text-h1 font-semibold"> Emails envoyés</div>
            <Space y={5} />
            {emails.length === 0 && (
              <div className="flex flex-col  w-fit rounded-xl items-center h-fit my-1 bg-neutral-50 p-2 border-l-3 border-neutral-400 ">
                <div className="text-h3 text-neutral-400 font-medium   gap-2 flex flex-row items-center">
                  <Icon1 icon_name="email" fill="grey" size={20} />
                  Aucun email envoyé pour ce dossier
                </div>
              </div>
            )}
            <div className="border-1 flex flex-col">
              {emails
                .sort((a, b) => b.date.seconds - a.date.seconds)
                .map((email, index) => (
                  <div
                    key={index}
                    className={`flex flex-col gap-1 ${index === email.length - 1 ? "" : "border-b-1"}  rounded-md bg-white`}
                  >
                    <EmailItem
                      email={email}
                      openedEmails={openedEmails}
                      setOpenedEmails={setOpenedEmails}
                    ></EmailItem>
                  </div>
                ))}
            </div>
            <Space y={30} />
          </div>
        </div>
        {/* Right Column: Fixed width */}
        <div className="flex flex-col gap-1 h-full max-w-c400 pl-2">
          <Space y={20} />
          <div className="text-h1 font-semibold">Informations</div>
          <Space y={5} />
          <div className="flex flex-col gap-1 w-full pr-2">
            <EditableInfoCell
              title="Numéro Devis"
              data={invoiceData?.num_devis}
              onChangeData={handleChangeNumDevis}
              handleSync={handleSyncNumDevis}
              orgData={orgData}
              folderData={invoiceData}
              restricted={false}
            />
            <InfoCell title="Création" data={invoiceData?.date} />
            <InfoCell
              title="Type"
              data={
                invoiceData?.client_type === "architect"
                  ? "Architecte/MO"
                  : "Particulier"
              }
            />
            <Space y={20} />
            <div className="text-h1 font-semibold"> Maître d'ouvrage</div>
            <Space y={5} />
            <InfoCell title="Nom" data={invoiceData?.nom_client} />
            <InfoCell title="Email" data={invoiceData?.email_client} />
            <InfoCell title="Téléphone" data={invoiceData?.tel_client} />
            <InfoCell title="Adresse" data={invoiceData?.adresse_client} />
            <InfoCell title="Postal" data={invoiceData?.postal_client} />
            <InfoCell title="Ville" data={invoiceData?.ville_client} />
            <Space y={20} />
            <div className="text-h1 font-semibold"> Projet</div>
            <Space y={5} />
            <InfoCell title="Adresse" data={invoiceData?.adresse_projet} />
            <InfoCell title="Postal" data={invoiceData?.postal_projet} />
            <InfoCell title="Ville" data={invoiceData?.ville_projet} />{" "}
            <Space y={20} />
            <div className="text-h1 font-semibold">Notes</div>
            {/* <textarea
              className={`w-full min-h-[100px] p-2 rounded-md focus:outline-none resize-y ${
                isEditingNotes ? "bg-white" : "bg-shadow_4"
              }`}
              value={invoiceData?.notes || ""}
              onChange={handleChangeNotes}
              onFocus={() => setIsEditingNotes(true)}
              onBlur={handleSyncNotes}
              placeholder="Ajouter des notes..."
            /> */}
            <NotesDevis
              invoice={invoiceData}
              userData={userData}
              orgData={orgData}
              onUpdate={handleFetchData}
            ></NotesDevis>
            <Space y={20} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;
export function limitStringSize(string, limit = 35) {
  return string.length > limit ? string.substring(0, limit) + "..." : string;
}

export const EmailItem = ({ email, openedEmails, setOpenedEmails }) => {
  return (
    <div className="flex flex-col">
      <div
        onClick={() => {
          if (openedEmails[email.id]) {
            let newOpened = { ...openedEmails };
            delete newOpened[email.id];
            setOpenedEmails(newOpened);
          } else {
            setOpenedEmails({
              ...openedEmails,
              [email.id]: true,
            });
          }
        }}
        className={`
        ${openedEmails[email.id] ? "bg-indigo-50 border-indigo-100 border-1" : "bg-white"}
        flex flex-col cursor-pointer hover:bg-neutral-50 hover:border-indigo-200 hover:border-1  shadow-md p-2`}
      >
        <div className="flex flex-row justify-between ">
          <div className="text-h3 ">{email.object}</div>
          <div className="text-h3 text-shadow_10">{email.id}</div>
          {/* <div
        className={`text-h3 text-white ${
          email.resend_email_status === "email.complained"
            ? "bg-red-400"
            : email.resend_email_status === "email.bounced"
              ? "bg-red-400"
              : email.resend_email_status === "email.delivered"
                ? "bg-green-400"
                : email.resend_email_status === "email.delivered_delayed"
                  ? "bg-orange-400"
                  : ""
        }`} 
      >
        {email.resend_email_status === "email.complained"
          ? "marqué comme spam"
          : email.resend_email_status === "email.bounced"
            ? "rejeté"
            : email.resend_email_status === "email.delivered"
              ? "reçu"
              : email.resend_email_status === "email.delivered_delayed"
                ? "temporairement bloqué"
                : ""}
      </div> */}
        </div>
        <div className="text-h3 flex flex-row   text-shadow_50 gap-2 items-center">
          <Icon1 icon_name="date" fill="grey" size={18}></Icon1>
          {format(email.date.toDate(), "dd/MM/yyyy HH:mm")}
        </div>
        {email.emails?.map((email, index) => (
          <div
            key={index}
            className="text-h3  text-shadow_50 flex flex-row gap-2 items-center "
          >
            <Icon1 icon_name="email" fill="grey" size={18}></Icon1>
            {email}
          </div>
        ))}
      </div>{" "}
      {openedEmails[email.id] && (
        <div className="text-h3 text-shadow_50 whitespace-pre-wrap p-4 ">
          {email.content?.includes("<!DOCTYPE html")
            ? "Rappel envoyé"
            : email.content}{" "}
        </div>
      )}
    </div>
  );
};
